import React, { createContext, useContext, useState, useEffect } from 'react';

// Available languages
export const languages = ['en', 'de', 'fr'];

// Default language
export const defaultLanguage = 'en';

// Translations object - will be populated from translation files
const translations = {
  // English translations (default)
  en: {
    'nav.features': 'Features',
    'nav.data': 'Weather Data',
    'nav.community': 'Community',
    'nav.about': 'About',
    'nav.getStarted': 'Get Started',
    'nav.userManual': 'User Manual',
    'lang.select': 'Select language',

    'hero.title': 'WeatherTeller',
    'hero.subtitle': 'Real-time weather intelligence, powered by community',
    'hero.description': 'From sunny picnic spots to hidden storm-watching perches, WeatherTeller doesn\'t just tell you the weather – it guides you to the perfect spot to experience it.',
    'hero.cta': 'Get Started',
    'hero.secondary': 'Learn More',

    'features.title': 'Why Choose WeatherTeller?',
    'features.item1': 'Instant access on any device - mobile, tablet, or desktop',
    'features.item2': 'Global coverage with local insights',
    'features.item3': 'Hyperlocal, real-time forecasts from your community',
    'features.item4': 'Share your weather experiences with photos and reports',
    'features.item5': 'Offline mode for reliable weather updates, even without internet',
    'features.item6': 'Visualize weather patterns with our interactive map',
    'features.item7': 'AI-powered predictions with increasing accuracy over time',
    'features.item8': 'Emoji-based weather representation for quick understanding',

    'data.title': 'Real-Time Weather Data',
    'data.subtitle': 'Powered by the community',
    'data.description': 'WeatherTeller transforms every user into a potential weather station, creating a dense network of real-time weather reporters that traditional forecasting can\'t match.',
    
    'community.title': 'Join Our Weather Community',
    'community.description': 'WeatherTeller is more than just an app—it\'s a community of weather enthusiasts helping each other make better decisions.',
    'community.join': 'Join Now',
    
    'manual.title': 'User Manual',
    'manual.subtitle': 'Everything you need to know about WeatherTeller',
    'manual.overview': 'Overview',
    'manual.features': 'Key Features',
    'manual.howItWorks': 'How It Works',
    'manual.userGuide': 'User Guide',
    'manual.gettingStarted': 'Getting Started',
    'manual.reportingWeather': 'Reporting Weather',
    'manual.usingMap': 'Using the Interactive Map',
    
    'cta.title': 'Ready to Experience WeatherTeller?',
    'cta.subtitle': 'Join thousands of users sharing real-time weather data',
    'cta.button': 'Get Started Now',
    
    'footer.rights': 'All rights reserved',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
    'footer.contact': 'Contact Us',
    
    // Donation translations
    'donation.title': 'Support WeatherTeller',
    'donation.subtitle': 'Help us improve weather data for everyone by supporting the development of new features and infrastructure',
    'donation.options.title': 'Choose Your Contribution',
    'donation.amount.10chf': 'Basic Support',
    'donation.amount.20chf': 'Standard Support',
    'donation.amount.50chf': 'Premium Support',
    'donation.amount.100chf': 'Gold Supporter',
    'donation.payment_methods.title': 'Select Payment Method',
    'donation.payment_methods.bank_transfer': 'Bank Transfer (preferred)',
    'donation.payment_methods.qr_bill': 'Swiss QR Bill',
    'donation.payment_methods.card': 'Credit Card',
    'donation.why.title': 'Why Support WeatherTeller?',
    'donation.why.reason1': 'Help build better weather data collection and analysis tools',
    'donation.why.reason2': 'Support ongoing server costs and infrastructure',
    'donation.why.reason3': 'Enable development of new features and capabilities',
    'donation.qr.scan': 'Scan the QR code to donate',
    'donation.bank_details.title': 'Bank Transfer Details',
    'donation.bank_details.account_holder': 'Account Holder',
    'donation.bank_details.iban': 'IBAN',
    'donation.bank_details.reference': 'Reference',
    'donation.bank_details.amount': 'Amount',
    'donation.thank_you.title': 'Thank You for Your Support!',
    'donation.thank_you.message': 'Your contribution helps us continue developing better weather data tools for everyone',
    'donation.confirmation.instructions': 'Please complete your donation using the banking details below',
    'donation.confirmation.thank_you': 'Thank you for your support!',
    'donation.button': 'Donate Now',
    'donation.return': 'Return to Donation Options',
    'donation.creditor.name': 'Pavel Maxim',
  },
  
  // German translations
  de: {
    'nav.features': 'Funktionen',
    'nav.data': 'Wetterdaten',
    'nav.community': 'Gemeinschaft',
    'nav.about': 'Über uns',
    'nav.getStarted': 'Jetzt starten',
    'nav.userManual': 'Benutzerhandbuch',
    'lang.select': 'Sprache auswählen',
    
    // Project Details Component
    'project.title': 'WeatherTeller: Echtzeit-Wetter-Intelligenz der Gemeinschaft',
    'project.motto': 'Echtzeit-Wetterinformationen, von der Community bereitgestellt',
    'project.introTitle': 'WeatherTeller: Echtzeit-Wetter-Intelligenz der Gemeinschaft',
    'project.introText1': 'WeatherTeller revolutioniert die Wettervorhersage durch Community- und IoT-Power. Unsere App kombiniert Crowdsourcing-Daten mit fortschrittlichen Algorithmen für hyperlokale Echtzeit-Wettereinblicke. Indem wir Benutzer und Geräte in Wetterstationen verwandeln, bieten wir unerreichte Genauigkeit für den persönlichen Gebrauch und für Industrien.',
    'project.introText2': 'Dies ist nicht nur eine App - es ist eine Bewegung hin zu kollektiver Intelligenz, Umweltverständnis und Community-Empowerment. Werden Sie Teil unserer Initiative für ein genaueres und reaktionsfähigeres Wetter-Informations-Ökosystem.',
    'project.approachTitle': 'Unser innovativer Ansatz',
    'project.approachText': 'WeatherTeller ist eine innovative mobile Anwendung, die darauf abzielt, zu revolutionieren, wie Menschen auf Wetterinformationen zugreifen und diese teilen. Durch die Kombination von nutzergenerierten Inhalten, künstlicher Intelligenz und Community-Validierung bietet WeatherTeller hyperlokale Echtzeit-Wetterberichte, die genauer und relevanter sind als traditionelle Wettervorhersagen.',
    'project.problemTitle': 'Das Problem',
    'project.problemText': 'Die aktuellen Wettervorhersagemethoden mangeln an hyperlokaler Genauigkeit und Echtzeit-Updates, was zu unzuverlässigen Vorhersagen für persönliche und berufliche Entscheidungsfindung führt. Traditionelle Wetter-Apps erfassen keine Mikroklima-Variationen und plötzliche Änderungen, was verschiedene Sektoren von der Landwirtschaft bis zur Veranstaltungsplanung beeinträchtigt.',
    'project.solutionTitle': 'Unsere Lösung',
    'project.solutionText': 'WeatherTeller ist eine gemeinschaftsgesteuerte Wettervorhersageplattform, die Crowdsourcing-Daten mit fortschrittlichen Algorithmen kombiniert, um hyperlokale Echtzeit-Wettereinblicke zu bieten. Zu den Hauptfunktionen gehören:',
    'project.solutionItem1': 'Nutzergenerierte Wetterberichte für dichte Datenerfassung',
    'project.solutionItem2': 'Maschinenlernalgorithmen für verbesserte lokale Vorhersagen',
    'project.solutionItem3': 'Gamification zur Förderung aktiver Nutzerbeteiligung',
    'project.solutionItem4': 'Vertrauensbasierter Validierungsmechanismus für Datenqualität',
    'project.solutionItem5': 'API für branchenübergreifende Anwendungen',
    'project.solutionText2': 'Indem wir jeden Benutzer in eine potenzielle Wetterstation verwandeln, demokratisiert WeatherTeller die Wetterintelligenz und verbessert die Genauigkeit und Aktualität von Vorhersagen.',
    'project.featuresTitle': 'Hauptfunktionen',
    'project.featuresItem1': 'Echtzeit, hyperlokale Wetterberichte von Benutzern',
    'project.featuresItem2': 'Visuelle Bestätigung mit emoji-basierten Wetteranzeigen',
    'project.featuresItem3': 'Community-Validierungssystem zur Sicherstellung der Datenqualität',
    'project.featuresItem4': 'Personalisierte Wetterwarnungen basierend auf Ihrem Standort',
    'project.featuresItem5': 'Beitragserkennungssystem mit Bestenliste',
    'project.featuresItem6': 'Interaktive Kartenoberfläche für einfache Navigation',
    'project.featuresItem7': 'Historische Datenverfolgung und Trendanalyse',
    'project.featuresText': 'Diese Funktionen machen WeatherTeller nicht nur zu einer Wetter-App, sondern zu einer sozialen Plattform für Wetterenthusiasten und alle, die auf genaue Wetterinformationen angewiesen sind.',
    'project.benefitsTitle': 'Wer profitiert',
    'project.benefitsItem1': 'Outdoor-Enthusiasten: Wanderer, Radfahrer, Skifahrer, Strandbesucher',
    'project.benefitsItem2': 'Landwirtschaftliche Fachleute: Landwirte, Ernteverwalter, landwirtschaftliche Berater',
    'project.benefitsItem3': 'Eventplaner: Hochzeitskoordinatoren, Festivalorganisatoren, Sportveranstaltungsmanager',
    'project.benefitsItem4': 'Fotografen: Landschafts-, Outdoor- und Veranstaltungsfotografen',
    'project.benefitsItem5': 'Tourismusindustrie: Hotels, Reiseveranstalter, Touristenattraktionen',
    'project.benefitsItem6': 'Forscher: Klimaforscher, Umweltwissenschaftler',
    'project.benefitsItem7': 'Notfalldienste: Katastrophenmanagementteams, Ersthelfer',
    'project.benefitsText': 'Unser Team vereint Fachwissen in Softwareentwicklung, KI und Benutzererfahrungsdesign. Unter der Leitung von Pavel Maxim sind wir bestrebt, die Art und Weise zu verändern, wie Menschen mit Wetterinformationen interagieren.',
    'project.joinTitle': 'Werden Sie Teil der Wetterrevolution',
    'project.joinText1': 'WeatherTeller stellt einen bedeutenden Sprung vorwärts in der Wettervorhersagetechnologie dar. Durch die Nutzung der Macht der gemeinschaftsgesteuerten Datensammlung und künstlichen Intelligenz sind wir in der Lage, beispiellose hyperlokale Wetterinformationen bereitzustellen.',
    'project.joinText2': 'Werden Sie Teil unserer wachsenden Gemeinschaft von Wetterreportern und helfen Sie mit, das genaueste Echtzeit-Wetterinformationssystem zu erstellen, das je entwickelt wurde. Ihre Beiträge machen einen Unterschied!',
    'project.joinText3': 'Starten Sie noch heute, indem Sie auf die Schaltfläche unten klicken.',
    'project.getStarted': 'Jetzt starten',
    'project.processing': 'Verarbeitung',
    'project.continue': 'Fortfahren',
    'project.infoPage': 'Informationsseite',
    'project.backToInfo': '← Zurück zur Infoseite',
    'project.previous': '← Vorherige',
    'project.next': 'Nächste →',

    'hero.title': 'WeatherTeller',
    'hero.subtitle': 'Echtzeit-Wetterinformationen, von der Community bereitgestellt',
    'hero.description': 'Von sonnigen Picknickplätzen bis hin zu versteckten Sturmbeobachtungspunkten - WeatherTeller sagt Ihnen nicht nur das Wetter, sondern führt Sie zum perfekten Ort, um es zu erleben.',
    'hero.cta': 'Jetzt starten',
    'hero.secondary': 'Mehr erfahren',

    'features.title': 'Warum WeatherTeller wählen?',
    'features.item1': 'Sofortiger Zugriff auf jedem Gerät - Mobiltelefon, Tablet oder Desktop',
    'features.item2': 'Globale Abdeckung mit lokalen Einblicken',
    'features.item3': 'Hyperlokale Echtzeit-Vorhersagen von Ihrer Community',
    'features.item4': 'Teilen Sie Ihre Wettererlebnisse mit Fotos und Berichten',
    'features.item5': 'Offline-Modus für zuverlässige Wetterupdates, auch ohne Internet',
    'features.item6': 'Visualisieren Sie Wettermuster mit unserer interaktiven Karte',
    'features.item7': 'KI-gestützte Vorhersagen mit zunehmender Genauigkeit',
    'features.item8': 'Emoji-basierte Wetterdarstellung zum schnellen Verständnis',

    'data.title': 'Echtzeit-Wetterdaten',
    'data.subtitle': 'Von der Community bereitgestellt',
    'data.description': 'WeatherTeller verwandelt jeden Benutzer in eine potenzielle Wetterstation und schafft so ein dichtes Netzwerk von Echtzeit-Wetterberichterstattern, das herkömmliche Vorhersagen nicht erreichen können.',
    
    'community.title': 'Treten Sie unserer Wetter-Community bei',
    'community.description': 'WeatherTeller ist mehr als nur eine App - es ist eine Gemeinschaft von Wetterenthusiasten, die sich gegenseitig helfen, bessere Entscheidungen zu treffen.',
    'community.join': 'Jetzt beitreten',
    
    'manual.title': 'Benutzerhandbuch',
    'manual.subtitle': 'Alles, was Sie über WeatherTeller wissen müssen',
    'manual.overview': 'Überblick',
    'manual.features': 'Hauptfunktionen',
    'manual.howItWorks': 'Wie es funktioniert',
    'manual.userGuide': 'Benutzerführung',
    'manual.gettingStarted': 'Erste Schritte',
    'manual.reportingWeather': 'Wetter melden',
    'manual.usingMap': 'Verwendung der interaktiven Karte',
    
    'cta.title': 'Bereit, WeatherTeller zu erleben?',
    'cta.subtitle': 'Schließen Sie sich Tausenden von Benutzern an, die Echtzeit-Wetterdaten teilen',
    'cta.button': 'Jetzt starten',
    
    'footer.rights': 'Alle Rechte vorbehalten',
    // ... (existing German translations)
    
    'footer.privacy': 'Datenschutzrichtlinie',
    'footer.terms': 'Nutzungsbedingungen',
    'footer.contact': 'Kontakt',
    
    // Donation translations - German
    'donation.title': 'Unterstützen Sie WeatherTeller',
    'donation.subtitle': 'Helfen Sie uns, Wetterdaten für alle zu verbessern, indem Sie die Entwicklung neuer Funktionen und Infrastruktur unterstützen',
    'donation.options.title': 'Wählen Sie Ihren Beitrag',
    'donation.amount.10chf': 'Grundlegende Unterstützung',
    'donation.amount.20chf': 'Standard-Unterstützung',
    'donation.amount.50chf': 'Premium-Unterstützung',
    'donation.amount.100chf': 'Gold-Unterstützer',
    'donation.payment_methods.title': 'Zahlungsmethode auswählen',
    'donation.payment_methods.bank_transfer': 'Banküberweisung (bevorzugt)',
    'donation.payment_methods.qr_bill': 'Schweizer QR-Rechnung',
    'donation.payment_methods.card': 'Kreditkarte',
    'donation.why.title': 'Warum WeatherTeller unterstützen?',
    'donation.why.reason1': 'Helfen Sie beim Aufbau besserer Werkzeuge zur Sammlung und Analyse von Wetterdaten',
    'donation.why.reason2': 'Unterstützen Sie laufende Serverkosten und Infrastruktur',
    'donation.why.reason3': 'Ermöglichen Sie die Entwicklung neuer Funktionen und Fähigkeiten',
    'donation.qr.scan': 'Scannen Sie den QR-Code zum Spenden',
    'donation.bank_details.title': 'Bankverbindung',
    'donation.bank_details.account_holder': 'Kontoinhaber',
    'donation.bank_details.iban': 'IBAN',
    'donation.bank_details.reference': 'Verwendungszweck',
    'donation.bank_details.amount': 'Betrag',
    'donation.thank_you.title': 'Vielen Dank für Ihre Unterstützung!',
    'donation.thank_you.message': 'Ihr Beitrag hilft uns, bessere Wetterdatentools für alle weiter zu entwickeln',
    'donation.confirmation.instructions': 'Bitte schließen Sie Ihre Spende mit den nachstehenden Bankverbindungsdaten ab',
    'donation.confirmation.thank_you': 'Vielen Dank für Ihre Unterstützung!',
    'donation.button': 'Jetzt spenden',
    'donation.return': 'Zurück zu den Spendenoptionen',
    'donation.creditor.name': 'Pavel Maxim',
  },
  
  // French translations
  fr: {
    'nav.features': 'Fonctionnalités',
    'nav.data': 'Données météo',
    'nav.community': 'Communauté',
    'nav.about': 'À propos',
    'nav.getStarted': 'Commencer',
    'nav.userManual': 'Manuel d\'utilisation',
    'lang.select': 'Sélectionner la langue',
    
    // Project Details Component
    'project.title': 'WeatherTeller: Intelligence météorologique communautaire en temps réel',
    'project.motto': 'Informations météo en temps réel, alimentées par la communauté',
    'project.introTitle': 'WeatherTeller: Intelligence météorologique communautaire en temps réel',
    'project.introText1': 'WeatherTeller révolutionne la prédiction météorologique grâce à la communauté et à la puissance de l\'IoT. Notre application combine les données collaboratives avec des algorithmes avancés pour des analyses météorologiques hyperlocales en temps réel. En transformant les utilisateurs et les appareils en stations météorologiques, nous offrons une précision inégalée pour un usage personnel et industriel.',
    'project.introText2': 'Ce n\'est pas qu\'une simple application - c\'est un mouvement vers l\'intelligence collective, la compréhension environnementale et l\'autonomisation communautaire. Rejoignez-nous pour créer un écosystème d\'information météorologique plus précis et réactif.',
    'project.approachTitle': 'Notre approche innovante',
    'project.approachText': 'WeatherTeller est une application mobile innovante qui vise à révolutionner la façon dont les gens accèdent et partagent les informations météorologiques. En combinant le contenu généré par les utilisateurs, l\'intelligence artificielle et la validation communautaire, WeatherTeller fournit des bulletins météorologiques hyperlocaux en temps réel qui sont plus précis et pertinents que les prévisions météorologiques traditionnelles.',
    'project.problemTitle': 'Le problème',
    'project.problemText': 'Les méthodes actuelles de prévision météorologique manquent de précision hyperlocale et de mises à jour en temps réel, ce qui conduit à des prévisions peu fiables pour la prise de décision personnelle et professionnelle. Les applications météo traditionnelles ne parviennent pas à capturer les variations microclimatiques et les changements soudains, affectant divers secteurs de l\'agriculture à la planification d\'événements.',
    'project.solutionTitle': 'Notre solution',
    'project.solutionText': 'WeatherTeller est une plateforme de prévision météorologique communautaire qui combine les données collaboratives avec des algorithmes avancés pour fournir des informations météorologiques hyperlocales en temps réel. Les fonctionnalités clés comprennent:',
    'project.solutionItem1': 'Rapports météorologiques générés par les utilisateurs pour une collecte dense de données',
    'project.solutionItem2': 'Algorithmes d\'apprentissage automatique pour améliorer les prévisions locales',
    'project.solutionItem3': 'Ludification pour encourager la participation active des utilisateurs',
    'project.solutionItem4': 'Mécanisme de validation basé sur la confiance pour la qualité des données',
    'project.solutionItem5': 'API pour des applications intersectorielles',
    'project.solutionText2': 'En transformant chaque utilisateur en station météorologique potentielle, WeatherTeller démocratise l\'intelligence météorologique, améliorant la précision et la rapidité des prévisions.',
    'project.featuresTitle': 'Fonctionnalités clés',
    'project.featuresItem1': 'Rapports météorologiques hyperlocaux en temps réel des utilisateurs',
    'project.featuresItem2': 'Confirmation visuelle avec indicateurs météo basés sur des émojis',
    'project.featuresItem3': 'Système de validation communautaire pour assurer la qualité des données',
    'project.featuresItem4': 'Alertes météorologiques personnalisées basées sur votre emplacement',
    'project.featuresItem5': 'Système de reconnaissance des contributeurs avec classement',
    'project.featuresItem6': 'Interface cartographique interactive pour une navigation facile',
    'project.featuresItem7': 'Suivi des données historiques et analyse des tendances',
    'project.featuresText': 'Ces fonctionnalités font de WeatherTeller non seulement une application météo, mais une plateforme sociale pour les passionnés de météo et tous ceux qui comptent sur des informations météorologiques précises.',
    'project.benefitsTitle': 'Qui en bénéficie',
    'project.benefitsItem1': 'Amateurs de plein air: Randonneurs, cyclistes, skieurs, vacanciers de plage',
    'project.benefitsItem2': 'Professionnels de l\'agriculture: Agriculteurs, gestionnaires de cultures, consultants agricoles',
    'project.benefitsItem3': 'Planificateurs d\'événements: Coordinateurs de mariage, organisateurs de festivals, gestionnaires d\'événements sportifs',
    'project.benefitsItem4': 'Photographes: Paysage, plein air et photographes d\'événements',
    'project.benefitsItem5': 'Industrie du tourisme: Hôtels, tour-opérateurs, attractions touristiques',
    'project.benefitsItem6': 'Chercheurs: Chercheurs climatiques, scientifiques environnementaux',
    'project.benefitsItem7': 'Services d\'urgence: Équipes de gestion des catastrophes, premiers intervenants',
    'project.benefitsText': 'Notre équipe combine une expertise en développement logiciel, IA et conception d\'expérience utilisateur. Dirigée par Pavel Maxim, nous nous engageons à transformer la façon dont les gens interagissent avec les informations météorologiques.',
    'project.joinTitle': 'Rejoignez la révolution météorologique',
    'project.joinText1': 'WeatherTeller représente un bond en avant significatif dans la technologie de prévision météorologique. En exploitant la puissance de la collecte de données communautaire et de l\'intelligence artificielle, nous sommes en mesure de fournir des informations météorologiques hyperlocales sans précédent.',
    'project.joinText2': 'Rejoignez notre communauté grandissante de reporters météo et aidez à créer le système d\'information météorologique en temps réel le plus précis jamais développé. Vos contributions font la différence!',
    'project.joinText3': 'Commencez dès aujourd\'hui en cliquant sur le bouton ci-dessous.',
    'project.getStarted': 'Commencer',
    'project.processing': 'Traitement',
    'project.continue': 'Continuer',
    'project.infoPage': 'Page d\'information',
    'project.backToInfo': '← Retour à la page d\'info',
    'project.previous': '← Précédent',
    'project.next': 'Suivant →',

    'hero.title': 'WeatherTeller',
    'hero.subtitle': 'Informations météo en temps réel, alimentées par la communauté',
    'hero.description': 'Des lieux de pique-nique ensoleillés aux perchoirs cachés d\'observation de tempêtes, WeatherTeller ne vous indique pas seulement la météo – il vous guide vers l\'endroit parfait pour en faire l\'expérience.',
    'hero.cta': 'Commencer',
    'hero.secondary': 'En savoir plus',

    'features.title': 'Pourquoi choisir WeatherTeller?',
    'features.item1': 'Accès instantané sur tous les appareils - mobile, tablette ou ordinateur',
    'features.item2': 'Couverture mondiale avec des aperçus locaux',
    'features.item3': 'Prévisions hyperlocales en temps réel de votre communauté',
    'features.item4': 'Partagez vos expériences météorologiques avec des photos et des rapports',
    'features.item5': 'Mode hors ligne pour des mises à jour météorologiques fiables, même sans internet',
    'features.item6': 'Visualisez les modèles météorologiques avec notre carte interactive',
    'features.item7': 'Prévisions alimentées par l\'IA avec une précision croissante',
    'features.item8': 'Représentation météorologique basée sur des émojis pour une compréhension rapide',

    'data.title': 'Données météorologiques en temps réel',
    'data.subtitle': 'Alimentées par la communauté',
    'data.description': 'WeatherTeller transforme chaque utilisateur en station météorologique potentielle, créant ainsi un réseau dense de reporters météorologiques en temps réel que les prévisions traditionnelles ne peuvent égaler.',
    
    'community.title': 'Rejoignez notre communauté météo',
    'community.description': 'WeatherTeller est plus qu\'une simple application - c\'est une communauté d\'amateurs de météo qui s\'entraident pour prendre de meilleures décisions.',
    'community.join': 'Rejoindre maintenant',
    
    'manual.title': 'Manuel d\'utilisation',
    'manual.subtitle': 'Tout ce que vous devez savoir sur WeatherTeller',
    'manual.overview': 'Aperçu',
    'manual.features': 'Fonctionnalités clés',
    'manual.howItWorks': 'Comment ça marche',
    'manual.userGuide': 'Guide de l\'utilisateur',
    'manual.gettingStarted': 'Premiers pas',
    'manual.reportingWeather': 'Signaler la météo',
    'manual.usingMap': 'Utilisation de la carte interactive',
    
    'cta.title': 'Prêt à découvrir WeatherTeller?',
    'cta.subtitle': 'Rejoignez des milliers d\'utilisateurs partageant des données météo en temps réel',
    'cta.button': 'Commencer maintenant',
    
    // ... (existing French translations)
    
    'footer.rights': 'Tous droits réservés',
    'footer.privacy': 'Politique de confidentialité',
    'footer.terms': 'Conditions d\'utilisation',
    'footer.contact': 'Contactez-nous',
    
    // Donation translations - French
    'donation.title': 'Soutenez WeatherTeller',
    'donation.subtitle': 'Aidez-nous à améliorer les données météorologiques pour tous en soutenant le développement de nouvelles fonctionnalités et de l\'infrastructure',
    'donation.options.title': 'Choisissez votre contribution',
    'donation.amount.10chf': 'Soutien de base',
    'donation.amount.20chf': 'Soutien standard',
    'donation.amount.50chf': 'Soutien premium',
    'donation.amount.100chf': 'Supporter Or',
    'donation.payment_methods.title': 'Sélectionnez le mode de paiement',
    'donation.payment_methods.bank_transfer': 'Virement bancaire (préféré)',
    'donation.payment_methods.qr_bill': 'Facture QR suisse',
    'donation.payment_methods.card': 'Carte de crédit',
    'donation.why.title': 'Pourquoi soutenir WeatherTeller?',
    'donation.why.reason1': 'Aidez à construire de meilleurs outils de collecte et d\'analyse de données météorologiques',
    'donation.why.reason2': 'Soutenez les coûts des serveurs et de l\'infrastructure',
    'donation.why.reason3': 'Permettez le développement de nouvelles fonctionnalités et capacités',
    'donation.qr.scan': 'Scannez le code QR pour faire un don',
    'donation.bank_details.title': 'Coordonnées bancaires',
    'donation.bank_details.account_holder': 'Titulaire du compte',
    'donation.bank_details.iban': 'IBAN',
    'donation.bank_details.reference': 'Référence',
    'donation.bank_details.amount': 'Montant',
    'donation.thank_you.title': 'Merci pour votre soutien!',
    'donation.thank_you.message': 'Votre contribution nous aide à continuer à développer de meilleurs outils de données météorologiques pour tous',
    'donation.confirmation.instructions': 'Veuillez compléter votre don en utilisant les coordonnées bancaires ci-dessous',
    'donation.confirmation.thank_you': 'Merci pour votre soutien!',
    'donation.button': 'Faire un don maintenant',
    'donation.return': 'Retour aux options de don',
    'donation.creditor.name': 'Pavel Maxim',
  }
};

// Create the context
const LanguageContext = createContext();

// Language provider component
export const LanguageProvider = ({ children }) => {
  // Get browser language or stored preference
  const getBrowserLanguage = () => {
    // Try to get stored language preference
    const storedLanguage = localStorage.getItem('preferredLanguage');
    
    if (storedLanguage && languages.includes(storedLanguage)) {
      return storedLanguage;
    }
    
    // Try to get browser language
    const browserLang = navigator.language.split('-')[0];
    
    // Check if browser language is supported
    if (languages.includes(browserLang)) {
      return browserLang;
    }
    
    // Default to English
    return defaultLanguage;
  };
  
  // State for current language
  const [language, setLanguageState] = useState(defaultLanguage);
  
  // Set language on initial load
  useEffect(() => {
    setLanguageState(getBrowserLanguage());
  }, []);
  
  // Method to change language
  const setLanguage = (lang) => {
    if (languages.includes(lang)) {
      setLanguageState(lang);
      localStorage.setItem('preferredLanguage', lang);
      
      // Update HTML lang attribute for SEO
      document.documentElement.lang = lang;
    }
  };
  
  // Translate method
  const translate = (key, fallback = '') => {
    // Get translation for current language
    const translation = translations[language];
    
    // Return translation or fallback to English or provided fallback
    return translation[key] || translations[defaultLanguage][key] || fallback || key;
  };
  
  // Provider value
  const value = {
    language,
    setLanguage,
    translate,
    languages
  };
  
  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook for using language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  
  return context;
};